import React, { useState, useMemo, useEffect } from 'react'
import { Form, Button, Card, Badge, Modal, Stack } from 'react-bootstrap'
import { specificGuestLookup } from '../../../utilities/api'
import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { usePrintTickets } from '../../../providers/PrintTicketsProvider/PrintTicketsProvider'
import {
  formatDateTime,
  formatNumber,
  getSinglarOrPluralWord,
  getTimezoneDate,
  sortTickets,
  timezones,
  formatSectionRowSeat,
} from '../../../utilities/helpers'
import { Error } from '../Error'
import { Spinner } from '../../LoadingContainer/Spinner'

export default function PrintTicketsForOrder({ order }) {
  const { closeModal } = useModal()
  const {
    handlePrintPDF,
    preparingPDF,
    handlePrintThermal,
    preparingThermal,
    handlePrintAccessPassesPDF,
    handlePrintAccessPassesThermal,
    preparingAccessPassesPDF,
    preparingAccessPassesThermal,
  } = usePrintTickets()
  const [ticketsByEvent, setTicketsByEvent] = useState([])
  const [selectedTickets, setSelectedTickets] = useState([])
  const [accessPasses, setAccessPasses] = useState([])
  const [accessPassTemplate, setAccessPassTemplate] = useState(null)
  const [selectedAccessPasses, setSelectedAccessPasses] = useState([])
  const [totalTickets, setTotalTickets] = useState(undefined)

  // State state
  const [error, setError] = useState(false)
  const [loadingOrder, setLoadingOrder] = useState(true)
  const [displayEmail, setDisplayEmail] = useState('')
  const [holderName, setHolderName] = useState({
    firstName: '',
    lastName: '',
    email: '',
  })

  const calculateTotalTickets = (ticketsByEvents) => {
    let totalCount = 0
    for (const eventWithTickets of ticketsByEvents) {
      totalCount += eventWithTickets?.tickets?.length || 0
    }
    return totalCount
  }

  const getSpecificOrder = () => {
    setLoadingOrder(true)
    let apiQuery = {}
    apiQuery['orderId'] = order.id
    apiQuery['organizationUUID'] = order.organizationUUID

    specificGuestLookup(apiQuery)
      .then((res) => {
        const groupedTickets = [...(res.data.groupedByEvent || [])].sort(
          (a, b) => new Date(a.event?.start) - new Date(b.event?.start)
        )
        const orderEmail = res.data.email
        const passes = res.data.access_passes || []
        const template =
          res.data.access_pass_template || order?.access_pass_template || null

        if (!groupedTickets.length && !passes.length) {
          setError(true)
          return
        }

        setTicketsByEvent(groupedTickets)
        setTotalTickets(calculateTotalTickets(groupedTickets))
        setAccessPasses(passes)
        setAccessPassTemplate(template)
        setDisplayEmail(orderEmail)
        setHolderName({
          firstName: res.data.firstName || order?.firstName || '',
          lastName: res.data.lastName || order?.lastName || '',
          email: orderEmail || order?.email || '',
        })
      })
      .catch((err) => {
        console.log('catch: ', err)
        setError(true)
      })
      .finally(() => {
        setLoadingOrder(false)
      })
  }

  useEffect(() => {
    if (order) getSpecificOrder()
  }, [order])

  const getSelectedTicketCount = () => {
    return selectedTickets.length
  }

  const getSelectedAccessPassCount = () => selectedAccessPasses.length

  const getSelectedCount = () =>
    getSelectedTicketCount() + getSelectedAccessPassCount()

  const isPreparingPrint =
    preparingPDF ||
    preparingThermal ||
    preparingAccessPassesPDF ||
    preparingAccessPassesThermal

  const hasTickets = totalTickets > 0
  const hasAccessPasses = accessPasses.length > 0

  const clickPrintPDF = async () => {
    if (getSelectedTicketCount() > 0) {
      const events = ticketsByEvent.map((eventData) => eventData?.event)
      await handlePrintPDF(
        events,
        selectedTickets,
        {
          email: holderName.email || displayEmail || order?.email,
          firstName: holderName.firstName || order?.firstName,
          lastName: holderName.lastName || order?.lastName,
        },
        order?.purchasedByOrganizationName
      )
    }
    if (getSelectedAccessPassCount() > 0) {
      await handlePrintAccessPassesPDF(
        accessPassTemplate,
        selectedAccessPasses,
        {
          email: holderName.email || displayEmail || order?.email,
          firstName: holderName.firstName || order?.firstName,
          lastName: holderName.lastName || order?.lastName,
        }
      )
    }
  }

  const clickPrintThermal = async () => {
    if (getSelectedTicketCount() > 0) {
      const events = ticketsByEvent.map((eventData) => eventData?.event)
      await handlePrintThermal(events, selectedTickets)
    }
    if (getSelectedAccessPassCount() > 0) {
      await handlePrintAccessPassesThermal(
        accessPassTemplate,
        selectedAccessPasses
      )
    }
  }

  if (loadingOrder) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '200px' }}
      >
        <div className="text-center">
          <Spinner variant="dark" size="lg">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <div className="mt-3">Loading order...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Error
        title="Error loading order!"
        description={null}
        onClose={closeModal}
      />
    )
  }

  const itemLabel = hasAccessPasses && !hasTickets ? 'Access pass' : 'Ticket'
  const listDescription =
    hasAccessPasses && !hasTickets
      ? 'List of access passes associated to'
      : hasAccessPasses && hasTickets
        ? 'List of tickets and access passes associated to'
        : 'List of tickets associated to'

  return (
    <div
      className="h-100 d-flex d-flex-column"
      style={{ backgroundColor: '#FFFFFF', overflow: 'hidden' }}
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <div className=" d-flex flex-row gap-4 align-items-center justify-content-start">
          <Modal.Title className="h4 text-dark">View order</Modal.Title>
        </div>
      </Modal.Header>
      <div className=" mb-4 px-4 d-flex justify-content-between align-items-center">
        <p className="text-muted mb-0 small">
          {listDescription}{' '}
          <span className="text-primary fw-medium">{displayEmail}</span>
        </p>
        <span className="text-muted small">
          {hasAccessPasses && !hasTickets
            ? `Access passes total ${formatNumber(accessPasses.length)}`
            : hasAccessPasses && hasTickets
              ? `Tickets ${formatNumber(totalTickets)} · Passes ${formatNumber(
                  accessPasses.length
                )}`
              : `Tickets total ${formatNumber(totalTickets)}`}
        </span>
      </div>

      {hasTickets && (
        <SelectTicketsByEvent
          ticketsByEvent={ticketsByEvent}
          selectedTickets={selectedTickets}
          setSelectedTickets={setSelectedTickets}
        />
      )}

      {hasAccessPasses && (
        <SelectAccessPasses
          accessPassTemplate={accessPassTemplate}
          accessPasses={accessPasses}
          selectedAccessPasses={selectedAccessPasses}
          setSelectedAccessPasses={setSelectedAccessPasses}
          hideSelectAll={hasTickets}
        />
      )}

      <Modal.Footer className="border-0 pt-0 px-4 d-flex flex-row justify-content-between align-items-center w-100">
        <div className="text-muted">
          {formatNumber(getSelectedCount())}{' '}
          {getSinglarOrPluralWord(getSelectedCount(), itemLabel).toLowerCase()}{' '}
          selected
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            disabled={getSelectedCount() === 0 || isPreparingPrint}
            onClick={clickPrintPDF}
            style={{ minWidth: '100px' }}
          >
            {preparingPDF || preparingAccessPassesPDF ? (
              <Spinner variant="white" size="md" />
            ) : (
              'Print PDF'
            )}
          </Button>
          <Button
            variant="primary"
            disabled={getSelectedCount() === 0 || isPreparingPrint}
            onClick={clickPrintThermal}
          >
            {preparingThermal || preparingAccessPassesThermal ? (
              <Spinner variant="white" size="md" />
            ) : (
              'Print Thermal'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </div>
  )
}

export function SelectTicketsByEvent({
  ticketsByEvent,
  selectedTickets,
  setSelectedTickets,
  hideSelectAll = false,
}) {
  const allTicketIds = useMemo(
    () =>
      ticketsByEvent.flatMap((event) =>
        event.tickets.map((ticket) => ticket.id)
      ),
    [ticketsByEvent]
  )

  const allTicketObjects = useMemo(
    () => ticketsByEvent.flatMap((event) => event.tickets),
    [ticketsByEvent]
  )

  const globalCheckboxState = useMemo(() => {
    if (allTicketIds.length === 0) return false
    if (selectedTickets.length === allTicketIds.length) return true
    if (selectedTickets.length > 0) return 'indeterminate'
    return false
  }, [selectedTickets, allTicketIds])

  const handleTicketSelect = (ticketToToggle) => {
    setSelectedTickets((prevSelected) => {
      const isSelected = prevSelected.some(
        (ticket) => ticket.id === ticketToToggle.id
      )
      if (isSelected) {
        return prevSelected.filter((ticket) => ticket.id !== ticketToToggle.id)
      } else {
        return [...prevSelected, ticketToToggle]
      }
    })
  }

  const handleGlobalSelectAllChange = () => {
    if (selectedTickets.length === allTicketObjects.length) {
      // If all are selected, deselect all
      setSelectedTickets([])
    } else {
      setSelectedTickets(allTicketObjects)
    }
  }

  const handleEventSelectToggle = (
    ticketsInEvent,
    areAllCurrentlySelectedInEvent
  ) => {
    const eventTicketIds = ticketsInEvent.map((t) => t.id)

    if (areAllCurrentlySelectedInEvent) {
      setSelectedTickets((prev) =>
        prev.filter((ticket) => !eventTicketIds.includes(ticket.id))
      )
    } else {
      // Add only those not already selected to avoid duplicates
      const newTicketsToAdd = ticketsInEvent.filter(
        (eventTicket) => !selectedTickets.some((st) => st.id === eventTicket.id)
      )
      setSelectedTickets((prev) => [...prev, ...newTicketsToAdd])
    }
  }

  const getEventCheckboxState = (ticketsInEvent, selectedTickets) => {
    const ticketsInEventIds = ticketsInEvent.map((t) => t.id)
    const selectedInEventCount = selectedTickets.filter((selectedTicket) =>
      ticketsInEventIds.includes(selectedTicket.id)
    ).length

    if (ticketsInEvent.length === 0) {
      return false
    }
    if (selectedInEventCount === ticketsInEventIds.length) {
      return true
    }
    if (
      selectedInEventCount > 0 &&
      selectedInEventCount < ticketsInEventIds.length
    ) {
      return 'indeterminate'
    }
    return false
  }

  return (
    <Modal.Body className="py-3 px-4 d-flex flex-column">
      {!hideSelectAll && (
        <div className="d-flex align-items-center">
          <Form.Check
            type="checkbox"
            id="select-all"
            checked={globalCheckboxState === true}
            ref={(input) => {
              if (input) {
                input.indeterminate = globalCheckboxState === 'indeterminate'
              }
            }}
            onChange={handleGlobalSelectAllChange}
            label="Select all"
            className="fw-semibold"
          />
        </div>
      )}

      <div className="flex-grow-1">
        {ticketsByEvent.map((eventData, eventIndex) => {
          const ticketsInEvent = sortTickets(eventData.tickets)
          const eventCheckboxState = getEventCheckboxState(
            ticketsInEvent,
            selectedTickets
          )

          return (
            <Card
              key={eventIndex}
              className="mb-3"
              style={{ outline: '1px solid #E6E8EC', borderRadius: '8px' }}
            >
              <Card.Header
                className="d-flex align-items-start"
                style={{
                  backgroundColor: '#FCFCFD',
                  padding: '16px',
                  borderBottom: '1px solid #E6E8EC',
                }}
              >
                <Form.Check
                  type="checkbox"
                  id={`event-select-${eventIndex}`}
                  checked={eventCheckboxState === true}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate =
                        eventCheckboxState === 'indeterminate'
                    }
                  }}
                  onChange={() =>
                    handleEventSelectToggle(
                      ticketsInEvent,
                      eventCheckboxState === true
                    )
                  }
                  className="me-3"
                />
                <div>
                  <div
                    className="mb-1"
                    style={{
                      color: '#141416',
                      fontWeight: 500,
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      lineHeight: '24px',
                    }}
                  >
                    {eventData.event.name}
                  </div>

                  <div
                    className="small"
                    style={{
                      color: '#777E90',
                      fontFamily: 'Poppins',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '170%',
                    }}
                  >
                    {formatDateTime(
                      getTimezoneDate(
                        eventData.event.start,
                        timezones(eventData.event?.venue?.timezone)
                      )
                    )}
                  </div>
                </div>
              </Card.Header>
              <Card.Body
                className=" p-3 d-flex flex-column gap-3"
                style={{ backgroundColor: '#FFFFFF' }}
              >
                {ticketsInEvent.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <Form.Check
                      type="checkbox"
                      className="form-check-display m-0"
                      id={`ticket-${ticket.id}`}
                    >
                      <Form.Check.Input
                        type="checkbox"
                        checked={selectedTickets.some(
                          (selectedTicket) => selectedTicket.id === ticket.id
                        )}
                        onChange={() => handleTicketSelect(ticket)}
                        disabled={ticket.exchanged}
                      />
                      <Stack
                        direction="horizontal"
                        gap={3}
                        className="flex-grow-1"
                      >
                        <Form.Check.Label
                          id={`ticket-${ticket.id}`}
                          className="m-0 d-flex gap-3 w-100 h-100 align-items-center form-check-label-sm"
                        >
                          {formatSectionRowSeat(ticket)}
                        </Form.Check.Label>
                      </Stack>
                    </Form.Check>

                    <Stack direction="horizontal" gap={2}>
                      {ticket?.unsent && (
                        <Badge bg="warning" className="small">
                          Unsent
                        </Badge>
                      )}
                      {ticket?.wasPrinted && (
                        <Badge bg="secondary" className="small">
                          Printed
                        </Badge>
                      )}
                      {ticket?.wasScanned && (
                        <Badge bg="primary" className="small">
                          Scanned In
                        </Badge>
                      )}
                    </Stack>
                  </div>
                ))}
              </Card.Body>
            </Card>
          )
        })}
      </div>
    </Modal.Body>
  )
}

function formatAccessPassLabel(pass) {
  if (
    pass?.sectionNumber != null &&
    pass.sectionNumber !== '' &&
    !pass.generalAdmission
  ) {
    return formatSectionRowSeat(pass)
  }
  if (pass?.sectionNumber != null && pass.sectionNumber !== '') {
    return `General Admission: ${pass.sectionNumber}`
  }
  return pass?.checkInCode || pass?.name || 'Access Pass'
}

export function SelectAccessPasses({
  accessPassTemplate,
  accessPasses,
  selectedAccessPasses,
  setSelectedAccessPasses,
  hideSelectAll = false,
}) {
  const allPassIds = useMemo(
    () => accessPasses.map((pass) => pass.id),
    [accessPasses]
  )

  const globalCheckboxState = useMemo(() => {
    if (allPassIds.length === 0) return false
    if (selectedAccessPasses.length === allPassIds.length) return true
    if (selectedAccessPasses.length > 0) return 'indeterminate'
    return false
  }, [selectedAccessPasses, allPassIds])

  const handlePassSelect = (passToToggle) => {
    setSelectedAccessPasses((prevSelected) => {
      const isSelected = prevSelected.some(
        (pass) => pass.id === passToToggle.id
      )
      if (isSelected) {
        return prevSelected.filter((pass) => pass.id !== passToToggle.id)
      }
      return [...prevSelected, passToToggle]
    })
  }

  const handleGlobalSelectAllChange = () => {
    if (selectedAccessPasses.length === accessPasses.length) {
      setSelectedAccessPasses([])
    } else {
      setSelectedAccessPasses(accessPasses)
    }
  }

  const handleGroupSelectToggle = () => {
    if (selectedAccessPasses.length === accessPasses.length) {
      setSelectedAccessPasses([])
    } else {
      setSelectedAccessPasses(accessPasses)
    }
  }

  const groupCheckboxState = useMemo(() => {
    if (accessPasses.length === 0) return false
    if (selectedAccessPasses.length === accessPasses.length) return true
    if (selectedAccessPasses.length > 0) return 'indeterminate'
    return false
  }, [selectedAccessPasses, accessPasses])

  return (
    <Modal.Body className="py-3 px-4 d-flex flex-column">
      {!hideSelectAll && (
        <div className="d-flex align-items-center">
          <Form.Check
            type="checkbox"
            id="select-all-access-passes"
            checked={globalCheckboxState === true}
            ref={(input) => {
              if (input) {
                input.indeterminate = globalCheckboxState === 'indeterminate'
              }
            }}
            onChange={handleGlobalSelectAllChange}
            label="Select all"
            className="fw-semibold"
          />
        </div>
      )}

      <div className="flex-grow-1">
        <Card
          className="mb-3"
          style={{ outline: '1px solid #E6E8EC', borderRadius: '8px' }}
        >
          <Card.Header
            className="d-flex align-items-start"
            style={{
              backgroundColor: '#FCFCFD',
              padding: '16px',
              borderBottom: '1px solid #E6E8EC',
            }}
          >
            <Form.Check
              type="checkbox"
              id="access-pass-group-select"
              checked={groupCheckboxState === true}
              ref={(input) => {
                if (input) {
                  input.indeterminate = groupCheckboxState === 'indeterminate'
                }
              }}
              onChange={handleGroupSelectToggle}
              className="me-3"
            />
            <div>
              <div
                className="mb-1"
                style={{
                  color: '#141416',
                  fontWeight: 500,
                  fontFamily: 'Poppins',
                  fontSize: '14px',
                  lineHeight: '24px',
                }}
              >
                {accessPassTemplate?.name ||
                  accessPasses[0]?.name ||
                  'Access Pass'}
              </div>
              <div
                className="small"
                style={{
                  color: '#777E90',
                  fontFamily: 'Poppins',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '170%',
                }}
              >
                Access Pass
              </div>
            </div>
          </Card.Header>
          <Card.Body
            className=" p-3 d-flex flex-column gap-3"
            style={{ backgroundColor: '#FFFFFF' }}
          >
            {accessPasses.length === 0 ? (
              <div className="text-center text-muted py-3">
                No access passes found
              </div>
            ) : (
              accessPasses.map((pass) => (
                <div
                  key={pass.id}
                  className="d-flex justify-content-between align-items-center"
                >
                  <Form.Check
                    type="checkbox"
                    className="form-check-display m-0"
                    id={`access-pass-${pass.id}`}
                  >
                    <Form.Check.Input
                      type="checkbox"
                      checked={selectedAccessPasses.some(
                        (selected) => selected.id === pass.id
                      )}
                      onChange={() => handlePassSelect(pass)}
                      disabled={pass.status === 'revoked'}
                    />
                    <Stack
                      direction="horizontal"
                      gap={3}
                      className="flex-grow-1"
                    >
                      <Form.Check.Label
                        id={`access-pass-label-${pass.id}`}
                        className="m-0 d-flex gap-3 w-100 h-100 align-items-center form-check-label-sm"
                      >
                        {formatAccessPassLabel(pass)}
                      </Form.Check.Label>
                    </Stack>
                  </Form.Check>

                  <Stack direction="horizontal" gap={2}>
                    {pass?.status === 'revoked' && (
                      <Badge bg="secondary" className="small">
                        Revoked
                      </Badge>
                    )}
                    {pass?.status === 'expired' && (
                      <Badge bg="secondary" className="small">
                        Expired
                      </Badge>
                    )}
                    {pass?.status === 'active' && (
                      <Badge bg="success" className="small">
                        Active
                      </Badge>
                    )}
                  </Stack>
                </div>
              ))
            )}
          </Card.Body>
        </Card>
      </div>
    </Modal.Body>
  )
}
